import { useQuery } from '@tanstack/react-query';
import { api } from '@xtnd/web/shared/api';
import type { InvoiceTransaction, TransformedInvoice } from '../types';

export function useUsersQuery() {
    return useQuery({
        queryKey: ['juniper-users'],
        queryFn: async (): Promise<Record<number, string>> => {
            // Add cache-busting parameter to avoid 304 responses
            // The _t parameter is a timestamp that prevents cached responses
            const userArray = await api.get<Array<{ id: number, name: string }>>(`/juniper-query/users/names?_t=${Date.now()}`);
            // Convert array to Record<number, string> format
            const userRecord: Record<number, string> = {};
            if (userArray && Array.isArray(userArray)) {
                userArray.forEach(user => {
                    userRecord[user.id] = user.name;
                });
            }
            return userRecord;
        },
        staleTime: 5 * 60 * 1000, // 5 minutes
        gcTime: 10 * 60 * 1000, // 10 minutes
    });
}

/**
 * Hook that filters users to only show those who have created transactions
 * in the provided transaction array
 * Accepts both InvoiceTransaction and TransformedInvoice types
 */
export function useFilteredUsersQuery(transactions: (InvoiceTransaction | TransformedInvoice)[]) {
    return useQuery({
        queryKey: ['juniper-users', 'filtered', transactions.length],
        queryFn: async (): Promise<Record<number, string>> => {
            // First get all users
            const userArray = await api.get<Array<{ id: number, name: string }>>(`/juniper-query/users/names?_t=${Date.now()}`);
            // Convert array to Record<number, string> format
            const allUsers: Record<number, string> = {};
            if (userArray && Array.isArray(userArray)) {
                userArray.forEach(user => {
                    allUsers[user.id] = user.name;
                });
            }

            if (!transactions.length) {
                return {};
            }

            // Helper to get createdByUserId (works for both types)
            const getCreatedByUserId = (t: InvoiceTransaction | TransformedInvoice): string | undefined => {
                return 'createdByUserId' in t ? t.createdByUserId : undefined;
            };

            // Extract unique user IDs from transactions
            const userIdsFromTransactions = new Set<number>();

            transactions.forEach(transaction => {
                const operationalBill = transaction.operationalBill;
                const operationalBillUser = operationalBill?.user;
                const createdByUserId = getCreatedByUserId(transaction);

                // Extract user ID from operationalBill.user.id (primary source)
                const operationalBillUserId = operationalBillUser?.id;
                if (operationalBillUserId !== undefined && operationalBillUserId !== null) {
                    const userIdNum = Number(operationalBillUserId);
                    // Validate conversion was successful and is a valid positive number
                    if (!isNaN(userIdNum) && userIdNum > 0) {
                        userIdsFromTransactions.add(userIdNum);
                    }
                }

                // Check createdByUserId field (secondary source)
                if (createdByUserId) {
                    const userIdNum = Number(createdByUserId);
                    if (!isNaN(userIdNum) && userIdNum > 0) {
                        userIdsFromTransactions.add(userIdNum);
                    }
                }
            });

            // Filter users to only include those found in transactions
            const filteredUsers: Record<number, string> = {};
            Object.entries(allUsers || {}).forEach(([userIdStr, userName]) => {
                const userIdNum = Number(userIdStr);
                if (userIdsFromTransactions.has(userIdNum)) {
                    filteredUsers[userIdNum] = userName;
                }
            });

            return filteredUsers;
        },
        staleTime: 5 * 60 * 1000, // 5 minutes
        gcTime: 10 * 60 * 1000, // 10 minutes
        enabled: transactions.length > 0, // Only run when we have transactions
    });
}
